import React, { PropsWithChildren } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { client } from "@/generated/api/client.gen.ts";
import { config } from "@/config/config";
import { router } from "@/app/_providers/tanstack-router-provider.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      experimental_prefetchInRender: true,
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error)) {
          if (
            Number(error.response?.status) >= 400 &&
            Number(error.response?.status) < 500
          ) {
            return false;
          }
        }
        return failureCount <= 3;
      },
    },
  },
});

client.setConfig({
  baseURL: config.endpointUri,
  withCredentials: true,
  headers: {
    "Content-Type": "application/ld+json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

client.instance.interceptors.request.use((config) => {
  // TODO: Should not empty payload when method is post, put, patch. Because Axios remove content-type on header
  const isBodyEmpty =
    ["post", "put", "patch"].includes(config?.method?.toLowerCase() ?? "") &&
    !config.data;
  if (isBodyEmpty) config.data = {};

  return config;
});

client.instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      router.parseLocation().pathname !== "/login"
    ) {
      throw router.navigate({
        to: "/login",
        search: { callbackUrl: router.parseLocation().href },
        replace: true,
      });
    }

    return Promise.reject(error);
  }
);

export default function TanstackQueryProvider({ children }: PropsWithChildren) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
